import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Menu, X, LayoutDashboard, Users, Building2, Briefcase, Zap, Mail, PlugZap, Megaphone, Key, Scale, ChevronDown, ClipboardList } from "lucide-react";

const SECTIONS = [
  {
    title: "Geral",
    items: [
      { id: "overview", label: "Visão geral", icon: LayoutDashboard, testId: "nav-admin-overview" },
    ],
  },
  {
    title: "Pessoas",
    items: [
      { id: "users", label: "Utilizadores", icon: Users, testId: "nav-admin-users" },
      { id: "companies", label: "Empresas", icon: Building2, testId: "nav-admin-companies", badgeKey: "pendingCompanies" },
    ],
  },
  {
    title: "Conte\u00fado",
    items: [
      { id: "moderation", label: "Vagas", icon: Briefcase, testId: "nav-admin-moderation", badgeKey: "pendingJobs" },
      { id: "applications", label: "Candidaturas", icon: ClipboardList, testId: "nav-admin-applications" },
      { id: "legal", label: "P\u00e1ginas legais", icon: Scale, testId: "nav-admin-legal" },
    ],
  },
  {
    title: "Monetiza\u00e7\u00e3o",
    items: [
      { id: "boosts", label: "Destaques", icon: Zap, testId: "nav-admin-boosts" },
      { id: "ads", label: "Banners", icon: Megaphone, testId: "nav-admin-ads" },
    ],
  },
  {
    title: "Integra\u00e7\u00f5es",
    items: [
      { id: "keys", label: "Chaves API", icon: Key, testId: "nav-admin-keys" },
      { id: "connectors", label: "Conectores", icon: PlugZap, testId: "nav-admin-connectors" },
      { id: "emails", label: "Emails", icon: Mail, testId: "nav-admin-emails" },
    ],
  },
];

export function AdminSidebar({ current, onNavigate, badges = {} }) {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("f2j_admin_sidebar_collapsed") === "1"; } catch { return false; }
  });
  const [expandedGroups, setExpandedGroups] = useState(() => {
    try {
      const stored = localStorage.getItem("f2j_admin_sidebar_groups");
      return stored ? JSON.parse(stored) : {};
    } catch { return {}; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem("f2j_admin_sidebar_collapsed", collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed]);

  useEffect(() => {
    try { localStorage.setItem("f2j_admin_sidebar_groups", JSON.stringify(expandedGroups)); } catch { /* ignore */ }
  }, [expandedGroups]);

  const toggleGroup = (title) => {
    setExpandedGroups((g) => ({ ...g, [title]: g[title] === false }));
  };

  const isGroupOpen = (title) => expandedGroups[title] !== false; // default open

  const handleNav = (id) => {
    onNavigate(id);
    setMobileOpen(false);
  };

  const content = (
    <>
      <div className={`flex items-center justify-between px-4 py-4 border-b border-black/5 ${collapsed ? "px-2" : ""}`}>
        {!collapsed && (
          <p className="font-heading font-black tracking-tighter text-sm text-slate-900">Painel Admin</p>
        )}
        <button
          type="button"
          data-testid="admin-sidebar-toggle"
          onClick={() => setCollapsed((c) => !c)}
          className="hidden lg:inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors"
          aria-label={collapsed ? "Expandir menu" : "Colapsar menu"}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          className="lg:hidden inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-slate-900"
          aria-label="Fechar menu"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto py-3">
        {SECTIONS.map((group) => (
          <div key={group.title} className="mb-1">
            {!collapsed && (
              <button
                type="button"
                onClick={() => toggleGroup(group.title)}
                className="w-full flex items-center justify-between px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400 hover:text-slate-600 transition-colors"
              >
                <span>{group.title}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${isGroupOpen(group.title) ? "" : "-rotate-90"}`} />
              </button>
            )}
            {(collapsed || isGroupOpen(group.title)) && (
              <ul className="space-y-0.5">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = current === item.id;
                  const badge = item.badgeKey ? badges[item.badgeKey] : 0;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        data-testid={item.testId}
                        onClick={() => handleNav(item.id)}
                        className={`w-full flex items-center gap-3 px-4 py-2 text-sm font-medium transition-colors relative ${
                          active
                            ? "bg-amber-50 text-amber-800 border-r-2 border-amber-500"
                            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                        } ${collapsed ? "justify-center px-2" : ""}`}
                        title={collapsed ? item.label : undefined}
                      >
                        <Icon className={`w-4 h-4 shrink-0 ${active ? "text-amber-600" : ""}`} />
                        {!collapsed && <span className="truncate">{item.label}</span>}
                        {!collapsed && badge > 0 && (
                          <span data-testid={`${item.testId}-badge`} className="ml-auto min-w-5 px-1.5 h-5 inline-flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-bold">
                            {badge}
                          </span>
                        )}
                        {collapsed && badge > 0 && (
                          <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-500" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ))}
      </nav>
    </>
  );

  return (
    <>
      {/* Mobile header */}
      <div className="lg:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-black/5">
        <button
          type="button"
          data-testid="admin-sidebar-mobile-toggle"
          onClick={() => setMobileOpen(true)}
          className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-slate-600 hover:bg-slate-100"
          aria-label="Abrir menu"
        >
          <Menu className="w-5 h-5" />
        </button>
        <p className="font-heading font-black tracking-tighter text-sm text-slate-900">Painel Admin</p>
        <div className="w-9" />
      </div>

      {/* Desktop sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={`hidden lg:flex flex-col bg-white border-r border-black/5 transition-all duration-200 sticky top-0 h-screen ${
          collapsed ? "w-16" : "w-64"
        }`}
      >
        {content}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="lg:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setMobileOpen(false)} />
          <aside className="lg:hidden fixed top-0 left-0 z-50 h-screen w-64 bg-white shadow-xl flex flex-col">
            {content}
          </aside>
        </>
      )}
    </>
  );
}
